import { useEffect, lazy, Suspense } from "react";
import HeroSection from "./home/HeroSection";
import WhyShortLet from "./home/WhyShortLet";
import IntroSection from "./home/IntroSection";
import ManchesterLandlordProof from "./manchester/ManchesterLandlordProof";
import WhatWeHandle from "./manchester/WhatWeHandle";

import ManchesterHowItWorks from "./manchester/ManchesterHowItWorks";
import ManchesterReviews from "./manchester/ManchesterReviews";

// Lazy load all below-fold sections for better initial page load
const PricingTeaser = lazy(() => import("./home/PricingTeaser"));
const LocationsCarousel = lazy(() => import("./home/LocationsCarousel"));
const NewListingsSection = lazy(() => import("./home/NewListingsSection"));
const ResourcesSection = lazy(() => import("./home/ResourcesSection"));
const FAQSection = lazy(() => import("./home/FAQSection"));

export default function Home() {
  useEffect(() => {
    // SEO title and meta description
    document.title = "Airbnb & Short-let Management UK | From 12% + VAT | Truestays";

    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.name = name; document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setOgMeta = (property, content) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("property", property); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };

    setMeta("description", "Full-service Airbnb and short-let management across the UK from 12% + VAT. Fully hands-off, paid per booking, no long-term contracts. 35,000+ stays managed. Free income estimate.");
    setMeta("keywords", "Airbnb management UK, short-let management, property management Manchester, Liverpool, London, holiday let services, rental income optimization");
    setOgMeta("og:type", "website");
    setOgMeta("og:title", "Airbnb & Short-let Management UK | From 12% + VAT | Truestays");
    setOgMeta("og:description", "Full-service Airbnb and short-let management across the UK from 12% + VAT. Fully hands-off, paid per booking, no long-term contracts. 35,000+ stays managed. Free income estimate.");
    setOgMeta("og:url", window.location.origin + "/");
    
    // Twitter Card
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", "Airbnb & Short-let Management UK | From 12% + VAT | Truestays");
    setMeta("twitter:description", "Full-service Airbnb and short-let management across the UK from 12% + VAT. Fully hands-off, paid per booking, no long-term contracts. 35,000+ stays managed.");

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = window.location.origin + "/";

    // Schema.org JSON-LD
    const schemaId = "ts-schema-home";
    let existingScript = document.getElementById(schemaId);
    if (existingScript) existingScript.remove();
    const script = document.createElement("script");
    script.id = schemaId;
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        { "@type": "LocalBusiness", "@id": "#truestays", name: "Truestays", url: window.location.origin, description: "Award-winning Airbnb and short-let management company serving Manchester, Liverpool, London and 15+ UK cities", areaServed: [{ "@type": "Country", name: "United Kingdom" }], priceRange: "From 12% + VAT", aggregateRating: { "@type": "AggregateRating", ratingValue: "5.0", reviewCount: "90", bestRating: "5" } },
        { "@type": "BreadcrumbList", itemListElement: [{ "@type": "ListItem", position: 1, name: "Home", item: window.location.origin + "/" }] }
      ]
    });
    document.head.appendChild(script);
    return () => { const s = document.getElementById(schemaId); if (s) s.remove(); };
  }, []);

  return (
    <>
      <HeroSection />
      <IntroSection />
      <ManchesterLandlordProof />
      <WhatWeHandle />
      <WhyShortLet />
      <Suspense fallback={<div style={{ height: 500 }} />}>
        <NewListingsSection />
      </Suspense>
      <ManchesterHowItWorks />
      <Suspense fallback={<div style={{ height: 300 }} />}>
        <PricingTeaser />
      </Suspense>
      <ManchesterReviews />
      <Suspense fallback={<div style={{ height: 400 }} />}>
        <LocationsCarousel />
      </Suspense>
      <Suspense fallback={<div style={{ height: 400 }} />}>
        <ResourcesSection />
      </Suspense>
      <Suspense fallback={<div style={{ height: 500 }} />}>
        <FAQSection />
      </Suspense>
    </>
  );
}